import axios from "axios";
import React, { useEffect, useState, useRef } from 'react'
import { Row, Col, Form, Button, Image, Spinner } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import Card from '../../../components/Card'
import { error, success } from "../../../notify";

const SignIn = () => {
   let history = useNavigate()
   const ERR_NETWORK_MESSAGE = 'Server is not available.'
   const [email, setEmail] = useState('')
   const [password, setPassword] = useState('')
   const [step, setStep] = useState('login') // 'login', 'qrcode', 'otp'
   const [qrBase64, setQrBase64] = useState('')
   const [otpCode, setOtpCode] = useState(['', '', '', '', '', ''])
   const [userId, setUserId] = useState(null)
   const [isSubmitting, setIsSubmitting] = useState(false)
   
   // Create refs for each OTP input box properly
   const inputRef1 = useRef(null);
   const inputRef2 = useRef(null);
   const inputRef3 = useRef(null);
   const inputRef4 = useRef(null);
   const inputRef5 = useRef(null);
   const inputRef6 = useRef(null);
   
   // Create an array of refs after declaring them individually
   const otpInputRefs = [inputRef1, inputRef2, inputRef3, inputRef4, inputRef5, inputRef6];

   let token = sessionStorage.getItem("revizeTrainingToken");

   useEffect(() => {
      if (token) {
         history('/dashboard');
      }
   }, [token, history]);

   const hanldeEmailChange = (e) => {
      e.persist();
      setEmail(e.target.value);
   }

   const hanldePasswordChange = (e) => {
      e.persist();
      setPassword(e.target.value);
   }

   // Handle OTP input changes
   const handleOtpChange = (e, index) => {
      if (isSubmitting) return;
      e.persist();
      const value = e.target.value;
      
      // Only accept numbers
      if (value && !/^\d+$/.test(value)) return;
      
      // Create a new array with the updated value
      const newOtpCode = [...otpCode];
      // Take only the last character if multiple characters are pasted
      newOtpCode[index] = value.slice(-1);
      setOtpCode(newOtpCode);
      
      // Auto focus to next input if value is entered
      if (value && index < 5 && otpInputRefs[index + 1].current) {
         otpInputRefs[index + 1].current.focus();
      }
   }

   // Handle key down in OTP fields (for backspace navigation)
   const handleOtpKeyDown = (e, index) => {
      // Move to previous input on backspace if current input is empty
      if (e.key === 'Backspace' && !otpCode[index] && index > 0 && otpInputRefs[index - 1].current) {
         otpInputRefs[index - 1].current.focus();
      }
   }

   // Handle paste event for OTP
   const handleOtpPaste = (e) => {
      if (isSubmitting) return;
      e.preventDefault();
      const pasteData = e.clipboardData.getData('text').trim();
      
      // Check if pasted content is a 6-digit number
      if (/^\d{6}$/.test(pasteData)) {
         const digits = pasteData.split('');
         setOtpCode(digits);
         
         // Focus the last input
         if (otpInputRefs[5].current) {
            otpInputRefs[5].current.focus();
         }
      }
   }

   // Check if device is remembered and 2FA token is still valid
   const checkRememberedDevice = (userId) => {
      const rememberedData = localStorage.getItem(`2fa_remembered_${userId}`);
      
      if (rememberedData) {
         try {
            const data = JSON.parse(rememberedData);
            const now = new Date().getTime();
            
            // Check if the token is still valid (less than 1 month old)
            if (data.expiresAt > now) {
               return true;
            } else {
               // Remove expired token
               localStorage.removeItem(`2fa_remembered_${userId}`);
            }
         } catch (e) {
            console.error("Error parsing remembered device data", e);
            localStorage.removeItem(`2fa_remembered_${userId}`);
         }
      }
      
      return false;
   }

   // Remember this device for 1 month
   const setDeviceAsRemembered = (userId) => {
      // Always remember the device for 1 month
      const now = new Date().getTime();
      const oneMonthInMs = 30 * 24 * 60 * 60 * 1000; // 30 days
   
      const rememberedData = {
         timestamp: now,
         expiresAt: now + oneMonthInMs,
         userId: userId
      };
   
      localStorage.setItem(`2fa_remembered_${userId}`, JSON.stringify(rememberedData));
   }
   

   const handleInitialLogin = async (e) => {
      e.preventDefault();
      if (isSubmitting) return;
      setIsSubmitting(true);

      const headers = { "Content-Type": "application/x-www-form-urlencoded" };
      var data = `grant_type=&username=${email}&password=${password}&scope=&client_id=&client_secret=`;

      try {
         const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/token`,
            data, { headers: headers });

         const responseData = response?.data;

         // Store userId for 2FA verification
         setUserId(responseData.id);

         // If 2FA is not enabled or device is remembered, directly log in the user
         if (!responseData.twoFAEnabled || 
             (responseData.twoFAEnabled && checkRememberedDevice(responseData.id))) {
            
            const token = responseData.access_token;
            
            if (!token) {
               error('Unable to login. Please try after some time.', true);
               return;
            }
            
            // Store user session data
            sessionStorage.clear();
            sessionStorage.setItem("revizeTrainingToken", token);
            sessionStorage.setItem("userRole", responseData.role);
            sessionStorage.setItem("firstName", responseData.firstName);
            sessionStorage.setItem("loggedInUserId", responseData.id);
            
            // Redirect to dashboard
            success("Login successful!", true);
            setTimeout(() => {
               history('/dashboard');
            }, 500);
         } else {
            // Continue with 2FA flow if it's enabled and device is not remembered
            await generate2faQR(responseData.id, email);
         }

      } catch (err) {
         if (err === 'ERR_NETWORK') {
            error(ERR_NETWORK_MESSAGE, true);
         } else {
            error(err.response?.data?.detail || "Login failed", true);
         }
      } finally {
         setIsSubmitting(false);
      }
   }

   const generate2faQR = async (userId, userEmail) => {
      try {
         const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/user/generate_totp_auth_uri`, {
            params: {
               user_id: userId,
               email: userEmail
            }
         });

         const data = response.data;

         if (data.is_2fa_enabled) {
            // If 2FA is already enabled, show only OTP input
            setStep('otp');
         } else {
            // If 2FA is not enabled, show QR code
            setQrBase64(data.qr_base64);
            setStep('qrcode');
         }
      } catch (err) {
         error("Failed to generate 2FA QR code", true);
         console.error(err);
      }
   }

   const verifyOtp = async (e) => {
      e.preventDefault();
      if (isSubmitting) return;
      setIsSubmitting(true);

      // Combine OTP digits into a single string
      const combinedOtp = otpCode.join('');
      
      try {
         const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/user/verify_totp`, {
            params: {
               user_id: userId,
               code: combinedOtp
            }
         });
   
         const data = response.data;
   
         if (data.isOTPvalid === true) {
            success("OTP verified successfully!", true);
            
            // Always remember this device by default
            setDeviceAsRemembered(userId);
            
            // Fetch user data again to get token
            const headers = { "Content-Type": "application/x-www-form-urlencoded" };
            var loginData = `grant_type=&username=${email}&password=${password}&scope=&client_id=&client_secret=`;
   
            const loginResponse = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/token`,
               loginData, { headers: headers });
   
            const responseData = loginResponse?.data;
            const token = responseData.access_token;
   
            if (!token) {
               error('Unable to login. Please try after some time.', true);
               // Clear OTP fields on failed login
               setOtpCode(['', '', '', '', '', '']);
               return;
            }
   
            sessionStorage.clear();
            sessionStorage.setItem("revizeTrainingToken", token);
            sessionStorage.setItem("userRole", responseData.role);
            sessionStorage.setItem("firstName", responseData.firstName);
            sessionStorage.setItem("loggedInUserId", responseData.id);
   
            setTimeout(() => {
               history('/dashboard');
            }, 500);
         } else {
            error("Invalid OTP code. Please try again.", true);
            // Clear OTP fields on invalid OTP
            setOtpCode(['', '', '', '', '', '']);
            // Focus the first input box
            if (inputRef1.current) {
               inputRef1.current.focus();
            }
         }
      } catch (err) {
         error("Invalid OTP entered.", true);
         console.error(err);
         // Clear OTP fields on error
         setOtpCode(['', '', '', '', '', '']);
         // Focus the first input box
         if (inputRef1.current) {
            inputRef1.current.focus();
         }
      } finally {
         setIsSubmitting(false);
      }
   }

   const handleCancel = () => {
      if (isSubmitting) return;
      setStep('login');
      setOtpCode(['', '', '', '', '', '']);
   }

   const areAllFieldsFilled = email !== "" && password !== "";
   const isOtpFilled = otpCode.every(digit => digit !== '');

   const renderSubmitLabel = (idleLabel, busyLabel) => (
      isSubmitting ? (
         <>
            <Spinner
               as="span"
               animation="border"
               size="sm"
               role="status"
               aria-hidden="true"
               className="me-2"
            />
            {busyLabel}
         </>
      ) : idleLabel
   )

   // Render login form
   const renderLoginForm = () => (
      <Form action="" onSubmit={handleInitialLogin}>
         <Row style={{ marginTop: "40px", marginBottom: "20px" }}>
            <Col lg="12">
               <Form.Group className="form-group" style={{ marginBottom: "30px" }}>
                  <Form.Control
                     onChange={(e) => hanldeEmailChange(e)}
                     type="email"
                     className=""
                     id="email"
                     aria-describedby="email"
                     placeholder="Email"
                     maxLength={100}
                     disabled={isSubmitting}
                     autoComplete="username"
                     style={{ height: "50px" }} />
               </Form.Group >
            </Col>
            <Col lg="12" className="">
               <Form.Group className="form-group">
                  <Form.Control
                     onChange={(e) => hanldePasswordChange(e)}
                     type="password"
                     className=""
                     id="password"
                     aria-describedby="password"
                     placeholder="Password"
                     maxLength={200}
                     disabled={isSubmitting}
                     autoComplete="current-password"
                     style={{ height: "50px" }} />
               </Form.Group>
            </Col>
            <Col lg="12" className="d-flex justify-content-between">
               <Link
                  to="/auth/recoverpw"
                  style={{ pointerEvents: isSubmitting ? 'none' : 'auto', opacity: isSubmitting ? 0.6 : 1 }}
               >
                  Forgot Password?
               </Link>
            </Col>
         </Row>
         <Col lg="12" className="">
            <Button
               disabled={!areAllFieldsFilled || isSubmitting}
               type="submit"
               variant="btn btn-primary"
               aria-busy={isSubmitting}
               style={{ height: "60px", width: "100%" }}
            >
               {renderSubmitLabel("SIGN IN", "Signing in...")}
            </Button>
         </Col>
      </Form>
   )

   const otpInputStyle = {
      width: "14%",
      height: "50px",
      textAlign: "center",
      fontSize: "1.2rem"
   };

   const renderOtpInputs = () => (
      <div className="d-flex justify-content-between">
         {otpCode.map((digit, index) => (
            <Form.Control
               key={index}
               ref={otpInputRefs[index]}
               type="text"
               inputMode="numeric"
               maxLength={1}
               value={digit}
               onChange={(e) => handleOtpChange(e, index)}
               onKeyDown={(e) => handleOtpKeyDown(e, index)}
               onPaste={index === 0 ? handleOtpPaste : undefined}
               disabled={isSubmitting}
               aria-label={`OTP digit ${index + 1}`}
               style={otpInputStyle}
            />
         ))}
      </div>
   )

   const renderOtpActions = () => (
      <div className="d-flex justify-content-between">
         <Button
            variant="outline-secondary"
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            style={{ height: "50px", width: "48%" }}
         >
            Cancel
         </Button>
         <Button
            type="submit"
            variant="primary"
            disabled={!isOtpFilled || isSubmitting}
            aria-busy={isSubmitting}
            style={{ height: "50px", width: "48%" }}
         >
            {renderSubmitLabel("Verify", "Verifying...")}
         </Button>
      </div>
   )

   // Render QR code screen with 6-digit OTP boxes
   const renderQrCode = () => (
      <div className="text-center">
         <h3 className="mb-4">Scan QR Code with Authenticator App</h3>
         {qrBase64 && (
            <div className="d-flex justify-content-center mb-4">
               <Image
                  src={`data:image/png;base64,${qrBase64}`}
                  alt="QR Code"
                  style={{ maxWidth: "200px" }}
               />
            </div>
         )}
         <p className="mb-4" style={{ fontSize: 18 }}>Scan this QR code with your authenticator app and enter the code below.</p>
         <Form onSubmit={verifyOtp}>
            <Form.Group className="mb-4">
               {renderOtpInputs()}
            </Form.Group>
            {renderOtpActions()}
         </Form>
      </div>
   )

   // Render OTP verification screen with 6-digit OTP boxes
   const renderOtpVerification = () => (
      <div className="text-left">
         <h3 className="mb-2 text-left">Two-Factor Authentication</h3>
         <p style={{ fontSize: 18, marginBottom: "40px" }}>Enter the 6-digit code from your authenticator app</p>
         <Form onSubmit={verifyOtp}>
            <Form.Group className="mb-4">
               {renderOtpInputs()}
            </Form.Group>
            {renderOtpActions()}
         </Form>
      </div>
   )

   useEffect(() => {
      // Focus on the first input field when the step changes
      if (step === 'login') {
        var emailInput = document.getElementById('email');
        if (emailInput) {
          emailInput.focus();
        }
      } else if (step === 'qrcode' || step === 'otp') {
        // Focus on first OTP input
        if (inputRef1.current) {
          inputRef1.current.focus();
        }
      }
    }, [step])
    
   return (
      <>
         {!token && (
            <section className="login-content" style={{ backgroundColor: "#e6e6e6" }}>
               <Link to="/dashboard" className="navbar-brand d-flex" style={{ padding: "10px", position: "absolute" }}>
                  <svg width="88.906" height="22.281" viewBox="0 0 88.906 22.281">
                     <path id="revizelogo" className="cls-1" d="M0.93,22H5.49v-5.94a6.067,6.067,0,0,1,1.3-4.29,4.7,4.7,0,0,1,3.585-1.38h0.24V5.624a4.6,4.6,0,0,0-3.24.9,6.688,6.688,0,0,0-1.89,2.64V5.924H0.93V22Zm14.73-9.3a4.666,4.666,0,0,1,1.155-2.49,3.062,3.062,0,0,1,2.325-.93,2.99,2.99,0,0,1,2.31.945A4.443,4.443,0,0,1,22.53,12.7H15.66Zm7.74,8.865a8.092,8.092,0,0,0,2.76-2.2l-2.61-2.31a6.939,6.939,0,0,1-1.86,1.26,5.083,5.083,0,0,1-2.04.39,4.085,4.085,0,0,1-2.565-.8,3.84,3.84,0,0,1-1.365-2.355H26.91q0.03-.33.045-0.63t0.015-.57a11.548,11.548,0,0,0-.465-3.285,8.058,8.058,0,0,0-1.44-2.79A7.081,7.081,0,0,0,22.62,6.344a7.834,7.834,0,0,0-3.48-.72,7.626,7.626,0,0,0-5.73,2.46,8.237,8.237,0,0,0-1.635,2.67,8.994,8.994,0,0,0-.585,3.24v0.06a8.708,8.708,0,0,0,.63,3.375,7.906,7.906,0,0,0,1.755,2.625,7.738,7.738,0,0,0,2.67,1.71,9.164,9.164,0,0,0,3.345.6A8.561,8.561,0,0,0,23.4,21.569Zm10.35,0.555h4.14l6.33-16.2H39.48l-3.63,10.77-3.6-10.77H27.42ZM45.57,22h4.56V5.924H45.57V22Zm-0.12-17.846h4.8V0.1h-4.8v4.05ZM52.38,22H66.36v-3.69H58.02l8.34-9.18V5.924H52.65v3.69h8.04l-8.31,9.18V22ZM72,12.7a4.666,4.666,0,0,1,1.155-2.49,3.062,3.062,0,0,1,2.325-.93,2.99,2.99,0,0,1,2.31.945,4.443,4.443,0,0,1,1.08,2.475H72Zm7.74,8.865a8.092,8.092,0,0,0,2.76-2.2l-2.61-2.31a6.939,6.939,0,0,1-1.86,1.26,5.083,5.083,0,0,1-2.04.39,4.085,4.085,0,0,1-2.565-.8,3.84,3.84,0,0,1-1.365-2.355h11.19q0.03-.33.045-0.63t0.015-.57a11.548,11.548,0,0,0-.465-3.285,8.058,8.058,0,0,0-1.44-2.79,7.081,7.081,0,0,0-2.445-1.935,7.834,7.834,0,0,0-3.48-.72,7.626,7.626,0,0,0-5.73,2.46,8.237,8.237,0,0,0-1.635,2.67,8.994,8.994,0,0,0-.585,3.24v0.06a8.708,8.708,0,0,0,.63,3.375,7.906,7.906,0,0,0,1.755,2.625,7.738,7.738,0,0,0,2.67,1.71,9.164,9.164,0,0,0,3.345.6A8.561,8.561,0,0,0,79.739,21.569ZM85.019,22h4.83V17.2h-4.83V22Z" transform="translate(-0.938 -0.094)"></path>
                  </svg>
               </Link>
               <Row className="m-0 align-items-center bg-grey vh-100" >
                  <Col md="12">
                     <Row className="justify-content-center">
                        <Col md="4">
                           <Card className="card-transparent shadow-none d-flex justify-content-center mb-0">
                              <Card.Body>
                                 <div style={{ padding: "140px 40px 40px 40px" }}>

                                    {step === 'login' && (
                                       <>
                                          <h3 className="mb-2 text-left">Hello! Let's get started</h3>
                                          <p className="text-left" style={{ fontSize: 18 }}>Sign in to continue.</p>
                                       </>
                                    )}

                                    {step === 'login' && renderLoginForm()}
                                    {step === 'qrcode' && renderQrCode()}
                                    {step === 'otp' && renderOtpVerification()}

                                 </div>
                              </Card.Body>
                           </Card>
                        </Col>
                     </Row>
                  </Col>
               </Row>
            </section>
         )}
      </>
   )
}

export default SignIn;